import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MenuBook as RecipeIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  AttachMoney as MoneyIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

import api from "../../store/services/api";
import InventoryService from "../../api/inventory";
import RecipeModal from "./modals/RecipeModal";
import ProductModal from "../../components/itemMenu/products/ProductModal";

const RecipesPage = ({ showAlert }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");

  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, page_size: rowsPerPage };
      if (search) params.title = search;

      const res = await api.get("/products/", { params });
      const data = res.data;

      if (data.results) {
        setProducts(data.results);
        setTotalCount(data.count || 0);
      } else if (Array.isArray(data)) {
        setProducts(data);
        setTotalCount(data.length);
      }
    } catch (err) {
      showAlert("Erro ao carregar produtos", "error");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, showAlert]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleOpenRecipe = async (product) => {
    setSelectedProduct(product);
    setRecipeModalOpen(true);
  };

  const handleRecipeSave = () => {
    setRecipeModalOpen(false);
    showAlert("Ficha tecnica salva com sucesso!");
    fetchProducts();
  };

  const handleCreateProduct = async (isEditing, product) => {
    try {
      const res = await api.post("/products/", product, {
        headers: { "Content-Type": "application/json" },
      });
      if (res.status === 201) {
        showAlert("Produto criado com sucesso!");
        setShowProductModal(false);
        fetchProducts();
      }
    } catch (err) {
      showAlert("Erro ao criar produto", "error");
    }
  };

  const formatMoney = (val) => {
    if (val === null || val === undefined) return "-";
    return Number(val).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={700}>
          Ficha Tecnica dos Produtos
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowProductModal(true)}
          sx={{
            backgroundColor: "#2563eb",
            "&:hover": { backgroundColor: "#1d4ed8" },
            textTransform: "none",
          }}
        >
          Novo Produto
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar produto..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.secondary", fontSize: 20 }} />
            ),
          }}
          sx={{ minWidth: 300 }}
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha("#2563eb", 0.04) }}>
              <TableCell>Produto</TableCell>
              <TableCell align="right">Preco de Venda</TableCell>
              <TableCell align="center">Receita</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  Carregando...
                </TableCell>
              </TableRow>
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  Nenhum produto encontrado.
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.product_id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {product.title}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatMoney(product.price)}</TableCell>
                  <TableCell align="center">
                    {product.recipe_items_count > 0 ? (
                      <Chip
                        icon={<RecipeIcon sx={{ fontSize: 14 }} />}
                        label={`${product.recipe_items_count} ingredientes`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        label="Sem receita"
                        size="small"
                        variant="outlined"
                        sx={{ color: "text.secondary" }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Editar ficha tecnica">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenRecipe(product)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por pagina"
        />
      </TableContainer>

      {/* Recipe Modal */}
      {selectedProduct && (
        <RecipeModal
          open={recipeModalOpen}
          onClose={() => {
            setRecipeModalOpen(false);
            setSelectedProduct(null);
          }}
          onSave={handleRecipeSave}
          product={selectedProduct}
          showAlert={showAlert}
        />
      )}

      {/* Product Creation Modal */}
      {showProductModal && (
        <ProductModal
          show={showProductModal}
          closeModal={() => setShowProductModal(false)}
          product={null}
          createProduct={handleCreateProduct}
        />
      )}
    </Box>
  );
};

export default RecipesPage;
